import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../services/localeHelpers';
import {
  Bug, ChevronDown, ChevronRight, Clock, Filter, Loader2,
  Monitor, Send, Smartphone, Tablet, Wifi, WifiOff,
  AlertTriangle, XCircle, Zap, CheckCircle2, Search as SearchIcon, Eye, MessageSquare, CheckCheck,
} from 'lucide-react';
import {
  collection, query, orderBy, limit, onSnapshot,
  doc, updateDoc, addDoc, where,
  type QueryConstraint,
} from 'firebase/firestore';
import { db, auth } from '../../../services/firebaseService';
import type { ErrorReportStatus, ErrorReportType } from '../../../services/errorReportService';

interface ErrorReportDoc {
  id: string;
  type: ErrorReportType;
  message: string;
  stack: string | null;
  componentStack: string | null;
  consoleLogs: Array<{ ts: number; level: string; message: string }>;
  url: string;
  userAgent: string;
  uid: string | null;
  email: string | null;
  appVersion: string | null;
  platform: 'web' | 'android' | 'ios';
  screenSize: string;
  isOnline: boolean;
  timestamp: string;
  status: ErrorReportStatus;
  adminNotes: string;
  resolvedBy: string | null;
  resolvedAt: string | null;
}

const STATUS_META: Record<ErrorReportStatus, { label: string; bg: string; text: string }> = {
  new:           { label: 'admin.errors.status_new',          bg: 'bg-red-500/15 border-red-500/30',     text: 'text-red-400' },
  investigating: { label: 'admin.errors.status_investigating', bg: 'bg-amber-500/15 border-amber-500/30', text: 'text-amber-400' },
  resolved:      { label: 'admin.errors.status_resolved',       bg: 'bg-emerald-500/15 border-emerald-500/30', text: 'text-emerald-400' },
  dismissed:     { label: 'admin.errors.status_dismissed',     bg: 'bg-zinc-500/15 border-zinc-500/30',   text: 'text-zinc-400' },
};

const TYPE_META: Record<ErrorReportType, { label: string; icon: React.ComponentType<any>; color: string }> = {
  crash:                { label: 'Crash',     icon: XCircle,       color: 'text-red-400' },
  unhandled_error:      { label: 'admin.errors.type_error',      icon: AlertTriangle, color: 'text-amber-400' },
  unhandled_rejection:  { label: 'Promise',   icon: Zap,           color: 'text-orange-400' },
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'nå';
  if (mins < 60) return `${mins}m siden`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}t siden`;
  const days = Math.floor(hours / 24);
  return `${days}d siden`;
}

function PlatformIcon({ platform }: { platform: string }) {
  if (platform === 'ios') return <Tablet size={12} />;
  if (platform === 'android') return <Smartphone size={12} />;
  return <Monitor size={12} />;
}

// Kjent benign støy: Firestore permission-denied fra gamle native-bundler
// (uten onSnapshot error-callback). Ingen brukerpåvirkning — skjules som standard.
function isPermissionNoise(message: string): boolean {
  const m = (message || '').toLowerCase();
  return (
    m.includes('missing or insufficient permissions') ||
    m.includes('permission-denied') ||
    m.includes('permission_denied')
  );
}

const AdminErrorReportsPanel: React.FC = () => {
  const { t } = useTranslation();
  const [reports, setReports] = useState<ErrorReportDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ErrorReportStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<ErrorReportType | 'all'>('all');
  const [editingNotes, setEditingNotes] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState<Record<string, string>>({});
  const [sendingFeedback, setSendingFeedback] = useState<string | null>(null);
  const [feedbackSent, setFeedbackSent] = useState<Set<string>>(new Set());
  const [showNoise, setShowNoise] = useState(false);

  // Real-time listener
  useEffect(() => {
    const constraints: QueryConstraint[] = [
      orderBy('timestamp', 'desc'),
      limit(200),
    ];

    const q = query(collection(db, 'error_reports'), ...constraints);
    const unsubscribe = onSnapshot(q, (snap) => {
      const docs: ErrorReportDoc[] = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
      } as ErrorReportDoc));
      setReports(docs);
      setLoading(false);
    }, (err) => {
      console.error('Error listening to error_reports:', err);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Client-side filtering
  const noiseCount = reports.filter(r => isPermissionNoise(r.message)).length;
  const visible = showNoise ? reports : reports.filter(r => !isPermissionNoise(r.message));
  const filtered = visible.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (typeFilter !== 'all' && r.type !== typeFilter) return false;
    return true;
  });

  const newCount = visible.filter(r => r.status === 'new').length;

  async function handleStatusChange(reportId: string, newStatus: ErrorReportStatus) {
    setSavingId(reportId);
    try {
      const updates: any = { status: newStatus };
      if (newStatus === 'resolved') {
        updates.resolvedBy = auth.currentUser?.email ?? null;
        updates.resolvedAt = new Date().toISOString();
      }
      await updateDoc(doc(db, 'error_reports', reportId), updates);
    } catch (e) {
      console.error('Failed to update error report status:', e);
    }
    setSavingId(null);
  }

  async function handleSaveNotes(reportId: string) {
    setSavingId(reportId);
    try {
      await updateDoc(doc(db, 'error_reports', reportId), {
        adminNotes: editingNotes[reportId] ?? '',
      });
    } catch (e) {
      console.error('Failed to save admin notes:', e);
    }
    setSavingId(null);
  }

  async function handleBulkResolve() {
    const newReports = visible.filter(r => r.status === 'new');
    if (newReports.length === 0) return;
    setSavingId('bulk');
    try {
      const updates = {
        status: 'resolved' as ErrorReportStatus,
        resolvedBy: auth.currentUser?.email ?? null,
        resolvedAt: new Date().toISOString(),
      };
      await Promise.all(newReports.map(r => updateDoc(doc(db, 'error_reports', r.id), updates)));
    } catch (e) {
      console.error('Failed to bulk resolve:', e);
    }
    setSavingId(null);
  }

  async function handleSendFeedback(report: ErrorReportDoc) {
    if (!report.uid || !feedbackText[report.id]?.trim()) return;
    setSendingFeedback(report.id);
    try {
      await addDoc(
        collection(db, 'users', report.uid, 'notifications'),
        {
          type: 'error_feedback',
          title: 'Tilbakemelding på feilrapport',
          body: feedbackText[report.id].trim(),
          data: { errorReportId: report.id },
          createdAt: Date.now(),
          isRead: false,
        }
      );
      setFeedbackSent(prev => new Set(prev).add(report.id));
      setFeedbackText(prev => ({ ...prev, [report.id]: '' }));
    } catch (e) {
      console.error('Failed to send feedback:', e);
    }
    setSendingFeedback(null);
  }

  const font = { fontFamily: '"Courier New", Courier, monospace' } as const;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 space-y-4">
        <Loader2 className="animate-spin text-red-400" size={32} />
        <p className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest">Laster feilrapporter...</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 w-full pb-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
          <Bug size={20} className="text-red-400" />
        </div>
        <div>
          <h3 className="text-sm font-black uppercase tracking-widest text-[var(--text-primary)]" style={font}>
            Feilrapporter
          </h3>
          <p className="text-2xs font-bold opacity-70 text-[var(--text-secondary)]" style={font}>
            {newCount > 0 ? `${newCount} nye · ` : ''}{visible.length} totalt
            {!showNoise && noiseCount > 0 ? ` · ${noiseCount} skjult` : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="flex items-center gap-1.5 text-[var(--text-muted)]">
          <Filter size={12} />
          <span className="text-3xs font-black uppercase tracking-widest">Status:</span>
        </div>
        {(['all', 'new', 'investigating', 'resolved', 'dismissed'] as const).map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-2.5 py-1 rounded-lg text-3xs font-black uppercase tracking-widest transition-all border ${
              statusFilter === s
                ? 'bg-red-600/20 text-red-300 border-red-500/30'
                : 'bg-[var(--bg-surface-alt)] text-[var(--text-muted)] border-[var(--border-secondary)] hover:bg-[var(--bg-surface)]'
            }`}
          >
            {s === 'all' ? t('common.all') : t(STATUS_META[s].label)}
            {s === 'new' && newCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-4xs">{newCount}</span>
            )}
          </button>
        ))}

        <div className="w-px bg-[var(--border-secondary)] mx-1" />

        <div className="flex items-center gap-1.5 text-[var(--text-muted)]">
          <span className="text-3xs font-black uppercase tracking-widest">Type:</span>
        </div>
        {(['all', 'crash', 'unhandled_error', 'unhandled_rejection'] as const).map(tp => (
          <button
            key={tp}
            onClick={() => setTypeFilter(tp)}
            className={`px-2.5 py-1 rounded-lg text-3xs font-black uppercase tracking-widest transition-all border ${
              typeFilter === tp
                ? 'bg-red-600/20 text-red-300 border-red-500/30'
                : 'bg-[var(--bg-surface-alt)] text-[var(--text-muted)] border-[var(--border-secondary)] hover:bg-[var(--bg-surface)]'
            }`}
          >
            {tp === 'all' ? t('common.all') : t(TYPE_META[tp].label)}
          </button>
        ))}

        {noiseCount > 0 && (
          <>
            <div className="w-px bg-[var(--border-secondary)] mx-1" />
            <button
              onClick={() => setShowNoise(v => !v)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-3xs font-black uppercase tracking-widest transition-all border ${
                showNoise
                  ? 'bg-red-600/20 text-red-300 border-red-500/30'
                  : 'bg-[var(--bg-surface-alt)] text-[var(--text-muted)] border-[var(--border-secondary)] hover:bg-[var(--bg-surface)]'
              }`}
              title="Kjent støy: permission-denied fra gamle app-versjoner uten brukerpåvirkning"
            >
              <Eye size={10} />
              Vis permission-støy ({noiseCount})
            </button>
          </>
        )}
      </div>

      {/* Bulk actions */}
      {newCount > 0 && (
        <button
          onClick={handleBulkResolve}
          disabled={savingId === 'bulk'}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest bg-emerald-600/80 hover:bg-emerald-500 text-white transition-all border border-emerald-500/30"
        >
          {savingId === 'bulk' ? <Loader2 size={12} className="animate-spin" /> : <CheckCheck size={12} />}
          Merk alle {newCount} nye som løst
        </button>
      )}

      {/* Report list */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-[var(--text-muted)]">
          <Bug size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-2xs font-black uppercase tracking-widest">Ingen feilrapporter</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(report => {
            const isExpanded = expandedId === report.id;
            const typeMeta = TYPE_META[report.type] || TYPE_META.unhandled_error;
            const statusMeta = STATUS_META[report.status] || STATUS_META.new;
            const TypeIcon = typeMeta.icon;

            return (
              <div
                key={report.id}
                className={`rounded-2xl border transition-all ${
                  isExpanded
                    ? 'bg-[var(--bg-surface-alt)] border-red-500/20'
                    : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)] hover:border-[var(--border-primary)]'
                }`}
              >
                {/* Row header */}
                <button
                  onClick={() => setExpandedId(isExpanded ? null : report.id)}
                  className="w-full flex items-center gap-3 p-4 text-left"
                >
                  <TypeIcon size={16} className={typeMeta.color} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-[var(--text-primary)] truncate" style={font}>
                      {report.message}
                    </p>
                    <div className="flex items-center gap-2 mt-1 text-[var(--text-muted)]">
                      <span className="text-3xs font-bold">{timeAgo(report.timestamp)}</span>
                      <span className="text-4xs">·</span>
                      <PlatformIcon platform={report.platform} />
                      <span className="text-3xs font-bold">{report.platform}</span>
                      {report.email && (
                        <>
                          <span className="text-4xs">·</span>
                          <span className="text-3xs font-bold truncate max-w-[120px]">{report.email}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-lg text-4xs font-black uppercase tracking-widest border ${statusMeta.bg} ${statusMeta.text}`}>
                    {t(statusMeta.label)}
                  </span>
                  {isExpanded ? <ChevronDown size={14} className="text-[var(--text-muted)]" /> : <ChevronRight size={14} className="text-[var(--text-muted)]" />}
                </button>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-4 border-t border-[var(--border-secondary)]">

                    {/* Status change */}
                    <div className="flex items-center gap-2 pt-3">
                      <span className="text-3xs font-black uppercase tracking-widest text-[var(--text-muted)]">Status:</span>
                      {(['new', 'investigating', 'resolved', 'dismissed'] as ErrorReportStatus[]).map(s => (
                        <button
                          key={s}
                          onClick={() => handleStatusChange(report.id, s)}
                          disabled={savingId === report.id}
                          className={`px-2 py-1 rounded-lg text-3xs font-black uppercase tracking-widest transition-all border ${
                            report.status === s
                              ? `${STATUS_META[s].bg} ${STATUS_META[s].text}`
                              : 'bg-transparent text-[var(--text-muted)] border-[var(--border-secondary)] hover:bg-[var(--bg-input)]'
                          }`}
                        >
                          {t(STATUS_META[s].label)}
                        </button>
                      ))}
                      {savingId === report.id && <Loader2 size={12} className="animate-spin text-[var(--text-muted)]" />}
                    </div>

                    {/* Error info */}
                    <div className="space-y-3">
                      {/* Message + Stack */}
                      <div className="bg-[var(--bg-input)] rounded-xl p-3 space-y-2">
                        <p className="text-3xs font-black uppercase tracking-widest text-red-400">Feilmelding</p>
                        <pre className="text-tiny text-[var(--text-primary)] whitespace-pre-wrap break-all font-mono leading-relaxed">
                          {report.message}
                        </pre>
                        {report.stack && (
                          <>
                            <p className="text-3xs font-black uppercase tracking-widest text-[var(--text-muted)] mt-2">Stack trace</p>
                            <pre className="text-2xs text-[var(--text-secondary)] whitespace-pre-wrap break-all font-mono leading-relaxed max-h-48 overflow-y-auto">
                              {report.stack}
                            </pre>
                          </>
                        )}
                        {report.componentStack && (
                          <>
                            <p className="text-3xs font-black uppercase tracking-widest text-[var(--text-muted)] mt-2">Component stack</p>
                            <pre className="text-2xs text-[var(--text-secondary)] whitespace-pre-wrap break-all font-mono leading-relaxed max-h-32 overflow-y-auto">
                              {report.componentStack}
                            </pre>
                          </>
                        )}
                      </div>

                      {/* Device info */}
                      <div className="bg-[var(--bg-input)] rounded-xl p-3">
                        <p className="text-3xs font-black uppercase tracking-widest text-sky-400 mb-2">Enhetsinformasjon</p>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-2xs font-mono">
                          <span className="text-[var(--text-muted)]">Platform</span>
                          <span className="text-[var(--text-primary)]">{report.platform}</span>
                          <span className="text-[var(--text-muted)]">Skjerm</span>
                          <span className="text-[var(--text-primary)]">{report.screenSize}</span>
                          <span className="text-[var(--text-muted)]">Online</span>
                          <span className="text-[var(--text-primary)] flex items-center gap-1">
                            {report.isOnline ? <Wifi size={10} className="text-emerald-400" /> : <WifiOff size={10} className="text-red-400" />}
                            {report.isOnline ? t('common.yes') : t('common.no')}
                          </span>
                          <span className="text-[var(--text-muted)]">Versjon</span>
                          <span className="text-[var(--text-primary)]">{report.appVersion ?? '–'}</span>
                          <span className="text-[var(--text-muted)]">Tidspunkt</span>
                          <span className="text-[var(--text-primary)]">{formatDate(report.timestamp)}</span>
                          {report.uid && (
                            <>
                              <span className="text-[var(--text-muted)]">Bruker</span>
                              <span className="text-[var(--text-primary)] truncate">{report.email || report.uid}</span>
                            </>
                          )}
                        </div>
                        {report.userAgent && (
                          <p className="text-3xs text-[var(--text-muted)] mt-2 break-all">{report.userAgent}</p>
                        )}
                      </div>

                      {/* Console logs */}
                      {report.consoleLogs && report.consoleLogs.length > 0 && (
                        <div className="bg-[var(--bg-input)] rounded-xl p-3">
                          <p className="text-3xs font-black uppercase tracking-widest text-violet-400 mb-2">
                            Konsoll-logg ({report.consoleLogs.length} oppføringer)
                          </p>
                          <div className="max-h-64 overflow-y-auto space-y-0.5 font-mono text-2xs">
                            {report.consoleLogs.map((log, i) => (
                              <div key={i} className="flex gap-2">
                                <span className="text-[var(--text-muted)] shrink-0 w-16">
                                  {new Date(log.ts).toLocaleTimeString(getDateLocale(), { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                </span>
                                <span className={`shrink-0 w-10 font-bold ${
                                  log.level === 'error' ? 'text-red-400' :
                                  log.level === 'warn' ? 'text-amber-400' :
                                  'text-[var(--text-muted)]'
                                }`}>
                                  {log.level}
                                </span>
                                <span className="text-[var(--text-secondary)] break-all">{log.message}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Admin notes */}
                      <div className="bg-[var(--bg-input)] rounded-xl p-3">
                        <p className="text-3xs font-black uppercase tracking-widest text-emerald-400 mb-2">Admin-notater</p>
                        <textarea
                          value={editingNotes[report.id] ?? report.adminNotes ?? ''}
                          onChange={e => setEditingNotes(prev => ({ ...prev, [report.id]: e.target.value }))}
                          placeholder="Skriv notater om denne feilen..."
                          className="w-full bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-lg p-2 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-none focus:outline-none focus:border-emerald-500/50"
                          rows={2}
                        />
                        {editingNotes[report.id] !== undefined && editingNotes[report.id] !== report.adminNotes && (
                          <button
                            onClick={() => handleSaveNotes(report.id)}
                            disabled={savingId === report.id}
                            className="mt-2 px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-emerald-50 text-3xs font-black uppercase tracking-widest border border-emerald-500 transition-colors"
                          >
                            {savingId === report.id ? t('common.saving') : t('admin.errors.save_notes')}
                          </button>
                        )}
                        {report.resolvedBy && (
                          <p className="mt-2 text-3xs text-[var(--text-muted)]">
                            Løst av {report.resolvedBy} · {report.resolvedAt ? formatDate(report.resolvedAt) : ''}
                          </p>
                        )}
                      </div>

                      {/* Send feedback to user */}
                      {report.uid && (
                        <div className="bg-[var(--bg-input)] rounded-xl p-3">
                          <p className="text-3xs font-black uppercase tracking-widest text-sky-400 mb-2 flex items-center gap-1.5">
                            <MessageSquare size={10} /> Tilbakemelding til bruker
                          </p>
                          {feedbackSent.has(report.id) && (
                            <div className="flex items-center gap-1.5 mb-2 text-emerald-400 text-2xs font-bold">
                              <CheckCircle2 size={12} /> Sendt!
                            </div>
                          )}
                          <div className="flex gap-2">
                            <input
                              value={feedbackText[report.id] ?? ''}
                              onChange={e => setFeedbackText(prev => ({ ...prev, [report.id]: e.target.value }))}
                              placeholder="F.eks. «Feilen er fikset i v1.0.96»"
                              className="flex-1 bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-lg px-3 py-1.5 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-sky-500/50"
                            />
                            <button
                              onClick={() => handleSendFeedback(report)}
                              disabled={sendingFeedback === report.id || !feedbackText[report.id]?.trim()}
                              className="px-3 py-1.5 rounded-lg bg-sky-700 hover:bg-sky-600 disabled:opacity-40 text-sky-50 text-3xs font-black uppercase tracking-widest border border-sky-500 transition-colors flex items-center gap-1.5"
                            >
                              {sendingFeedback === report.id ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                              Send
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminErrorReportsPanel;
